import * as THREE from 'three'

const vertexPars = /* glsl */`
  varying vec3 vTerrainWorldPosition;
  varying vec3 vTerrainWorldNormal;
  varying vec2 vTerrainUv;
`

const vertexNormal = /* glsl */`
  vTerrainWorldNormal = normalize(transpose(inverse(mat3(modelMatrix))) * objectNormal);
`

const vertexPosition = /* glsl */`
  vTerrainWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;
  vTerrainUv = uv;
`

const fragmentPars = /* glsl */`
  varying vec3 vTerrainWorldPosition;
  varying vec3 vTerrainWorldNormal;
  varying vec2 vTerrainUv;
  uniform sampler2D uAerialMap;
  uniform vec3 uForest;
  uniform vec3 uGranite;
  uniform vec3 uDeepNavy;
  uniform vec3 uContour;
  uniform vec3 uRust;
  uniform vec3 uIvory;
  uniform float uElevationMin;
  uniform float uElevationMax;
  uniform float uContourSpacing;
  uniform float uContourWidth;
  uniform float uRustSlopeStart;
  uniform float uSnowStart;
  uniform float uSnowSoftness;
  uniform float uAerialNear;
  uniform float uAerialFar;
  uniform float uAerialStrength;
  uniform float uContourNear;
  uniform float uContourFar;
  uniform float uFlipAerialY;
  uniform float uWeather;
  uniform float uTimeOfDay;
  uniform float uVisibility;
  uniform float uDetailStrength;
  uniform vec3 uSunDirection;

  float terrainBand(float value, float spacing, float width) {
    float coord = value / spacing;
    float edge = abs(fract(coord + 0.5) - 0.5);
    float aa = clamp(fwidth(coord), 0.0001, 0.075);
    return 1.0 - smoothstep(width, width + aa * 1.5, edge);
  }

  vec3 gradeAerial(vec3 source) {
    // Preserve geographic information while suppressing the raw satellite-map look.
    float luma = dot(source, vec3(0.2126, 0.7152, 0.0722));
    vec3 muted = mix(vec3(luma), source, 0.58);
    muted = (muted - 0.5) * 0.94 + 0.5;
    vec3 shadow = uDeepNavy * 1.45;
    vec3 light = mix(uGranite, uIvory, 0.22);
    vec3 duotone = mix(shadow, light, smoothstep(0.08, 0.88, luma));
    return mix(duotone, muted, 0.68);
  }
`

const fragmentMain = /* glsl */`
  vec3 terrainNormal = normalize(vTerrainWorldNormal);
  float elevation = clamp((vTerrainWorldPosition.y - uElevationMin) /
    max(uElevationMax - uElevationMin, 0.001), 0.0, 1.0);
  float upness = clamp(dot(terrainNormal, vec3(0.0, 1.0, 0.0)), 0.0, 1.0);
  float slope = 1.0 - upness;
  float viewDistance = length(vTerrainWorldPosition - cameraPosition);
  float weather = clamp(uWeather, 0.0, 1.0);
  float visibility = clamp(uVisibility, 0.0, 1.0);

  vec3 authoredBase = mix(uForest, uGranite, smoothstep(0.24, 0.77, elevation));
  authoredBase = mix(uDeepNavy, authoredBase, 0.82);

  vec2 aerialUv = vec2(vTerrainUv.x, mix(vTerrainUv.y, 1.0 - vTerrainUv.y, uFlipAerialY));
  vec3 aerialSource = texture2D(uAerialMap, aerialUv).rgb;
  vec3 aerial = gradeAerial(aerialSource);

  // Local contrast restores drainage, forest edge, and Boulder street structure
  // lost when the orthophoto is draped over a low-poly display mesh.
  vec2 detailStep = vec2(1.0 / 2048.0);
  float centerLuma = dot(aerialSource, vec3(0.2126, 0.7152, 0.0722));
  float neighborhood = 0.25 * (
    dot(texture2D(uAerialMap, aerialUv + vec2(detailStep.x, 0.0)).rgb, vec3(0.2126, 0.7152, 0.0722)) +
    dot(texture2D(uAerialMap, aerialUv - vec2(detailStep.x, 0.0)).rgb, vec3(0.2126, 0.7152, 0.0722)) +
    dot(texture2D(uAerialMap, aerialUv + vec2(0.0, detailStep.y)).rgb, vec3(0.2126, 0.7152, 0.0722)) +
    dot(texture2D(uAerialMap, aerialUv - vec2(0.0, detailStep.y)).rgb, vec3(0.2126, 0.7152, 0.0722))
  );
  aerial += vec3((centerLuma - neighborhood) * uDetailStrength);
  // Full geographic texture close up; it recedes into the authored palette at distance.
  float aerialRange = 1.0 - smoothstep(uAerialNear, uAerialFar, viewDistance);
  float grazingFade = smoothstep(0.02, 0.18, upness);
  float aerialMix = aerialRange * grazingFade * uAerialStrength * mix(1.0, 0.72, weather);
  vec3 terrainColor = mix(authoredBase, aerial, aerialMix);

  // A restrained raking-light pass makes the Flatirons and foothill folds legible
  // even before Three's scene lights are evaluated.
  float facing = dot(terrainNormal, normalize(uSunDirection));
  float raking = mix(0.78, 1.16, smoothstep(-0.62, 0.72, facing));
  float valleyOcclusion = mix(0.86, 1.0, smoothstep(0.08, 0.46, upness));
  terrainColor *= mix(1.0, raking * valleyOcclusion, 0.42);

  // BV color is semantic, not a wash: sandstone only appears on steep, lower faces.
  float rockMask = smoothstep(uRustSlopeStart, min(uRustSlopeStart + 0.24, 0.99), slope)
    * (1.0 - smoothstep(0.76, 0.96, elevation));
  terrainColor = mix(terrainColor, uRust, rockMask * (0.22 + 0.32 * (1.0 - aerialRange)));

  float snow = smoothstep(uSnowStart, uSnowStart + uSnowSoftness, elevation)
    * smoothstep(0.09, 0.5, upness);
  terrainColor = mix(terrainColor, uIvory, snow * (0.35 + 0.32 * (1.0 - aerialRange)));

  // Weather is a cool, low-contrast veil rather than a flat grey overlay.
  float cloudVeil = weather * smoothstep(0.28, 0.94, elevation);
  terrainColor = mix(terrainColor, mix(uGranite, uContour, 0.28), cloudVeil * 0.28);

  // Contours appear principally at mid/far range so close terrain remains photographic.
  float contour = terrainBand(vTerrainWorldPosition.y - uElevationMin, uContourSpacing, uContourWidth);
  float major = terrainBand(vTerrainWorldPosition.y - uElevationMin, uContourSpacing * 5.0, uContourWidth * 1.35);
  float contourRange = smoothstep(uContourNear, uContourFar, viewDistance);
  float contourMask = contourRange * visibility * (1.0 - snow * 0.55) * (1.0 - rockMask * 0.24);
  terrainColor = mix(terrainColor, uContour, contour * contourMask * 0.19);
  terrainColor = mix(terrainColor, uIvory, major * contourMask * 0.045);

  // Dawn/dusk hooks warm lit faces while preserving navy shadows and rope contrast.
  float goldenHour = 1.0 - abs(clamp(uTimeOfDay, 0.0, 1.0) * 2.0 - 1.0);
  terrainColor = mix(terrainColor, terrainColor * vec3(1.08, 1.015, 0.91), goldenHour * max(facing, 0.0) * 0.18);
  diffuseColor.rgb = clamp(terrainColor, 0.0, 1.0);
`

export function createAerialContourTerrainMaterial(aerialMap, options = {}) {
  aerialMap.colorSpace = THREE.SRGBColorSpace
  aerialMap.wrapS = aerialMap.wrapT = THREE.ClampToEdgeWrapping
  aerialMap.anisotropy = options.anisotropy ?? 8

  const material = new THREE.MeshStandardMaterial({
    // `map` also enables Three's UV shader path. Our injected color replaces the
    // stock map result below, so this does not visually apply the texture twice.
    map: aerialMap,
    color: '#667268', roughness: 0.91, metalness: 0, envMapIntensity: 0.18,
  })
  const uniforms = {
    uAerialMap: { value: aerialMap },
    uForest: { value: new THREE.Color(options.forest ?? '#445548') },
    uGranite: { value: new THREE.Color(options.granite ?? '#85877f') },
    uDeepNavy: { value: new THREE.Color(options.deepNavy ?? '#071738') },
    uContour: { value: new THREE.Color(options.contour ?? '#7e9daa') },
    uRust: { value: new THREE.Color(options.rust ?? '#a55736') },
    uIvory: { value: new THREE.Color(options.ivory ?? '#e5e2d8') },
    uElevationMin: { value: options.elevationMin ?? -5.2 },
    uElevationMax: { value: options.elevationMax ?? 23.5 },
    uContourSpacing: { value: options.contourSpacing ?? 0.58 },
    uContourWidth: { value: options.contourWidth ?? 0.026 },
    uRustSlopeStart: { value: options.rustSlopeStart ?? 0.51 },
    uSnowStart: { value: options.snowStart ?? 0.8 },
    uSnowSoftness: { value: options.snowSoftness ?? 0.12 },
    uAerialNear: { value: options.aerialNear ?? 30 },
    uAerialFar: { value: options.aerialFar ?? 115 },
    uAerialStrength: { value: options.aerialStrength ?? 0.82 },
    uContourNear: { value: options.contourNear ?? 55 },
    uContourFar: { value: options.contourFar ?? 145 },
    uFlipAerialY: { value: options.flipAerialY ? 1 : 0 },
    uWeather: { value: options.weather ?? 0 },
    uTimeOfDay: { value: options.timeOfDay ?? 0.32 },
    uVisibility: { value: options.visibility ?? 1 },
    uDetailStrength: { value: options.detailStrength ?? 0.62 },
    uSunDirection: { value: new THREE.Vector3(...(options.sunDirection ?? [-0.55, 0.72, 0.42])).normalize() },
  }
  material.userData.terrainUniforms = uniforms
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, uniforms)
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${vertexPars}`)
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>\n${vertexNormal}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${vertexPosition}`)
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${fragmentPars}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${fragmentMain}`)
  }
  material.customProgramCacheKey = () => 'bv-aerial-contour-v6-2'
  return material
}

/** Optional hook for future chapter/weather orchestration. Values are normalized. */
export function setAerialTerrainConditions(material, conditions = {}) {
  const uniforms = material?.userData?.terrainUniforms
  if (!uniforms) return
  if (conditions.weather != null) uniforms.uWeather.value = THREE.MathUtils.clamp(conditions.weather, 0, 1)
  if (conditions.timeOfDay != null) uniforms.uTimeOfDay.value = THREE.MathUtils.clamp(conditions.timeOfDay, 0, 1)
  if (conditions.visibility != null) uniforms.uVisibility.value = THREE.MathUtils.clamp(conditions.visibility, 0, 1)
  if (conditions.snowStart != null) uniforms.uSnowStart.value = conditions.snowStart
  if (conditions.sunDirection) uniforms.uSunDirection.value.fromArray(conditions.sunDirection).normalize()
}
